import { useEffect, useState } from "react";
import StepSelectProcedureV2 from "./StepSelectProcedureV2";
import StepSelectProcedureV2Treatment from "./StepSelectProcedureV2Treatment";
import { useCampaignFocus } from "../hooks/useCampaignFocus";
import {
  oncePerSession,
  trackQuoteFocusFallback,
} from "../../../utils/quoteAnalytics";
import { useIsMobile } from "../MobileBreakpointContext";

type StepSelectProcedureProps = {
  next: () => void;
};

/**
 * Procedure-step dispatcher for focused campaign mode.
 *
 * The procedure-focus-v1 A/B test is CONCLUDED (focused won) — focused mode
 * now serves 100% of campaign-eligible traffic with no A/B enrollment. We no
 * longer call useABTestVariant: the bucket was sticky per device for 365 days,
 * so any visitor assigned "control" while the test ran would have stayed on
 * the full menu indefinitely even after the ramp to 100%. Resolving the test
 * (abTestConfigs) is belt-and-suspenders for any lingering stored buckets;
 * this dispatcher just stops consulting them.
 *
 * Non-campaign traffic (no usable focus_types[]/focus_procedures[] in the URL)
 * still renders the default StepSelectProcedureV2 untouched. Only eligible
 * campaign traffic renders the focused treatment.
 */
const StepSelectProcedure = ({ next }: StepSelectProcedureProps) => {
  const focus = useCampaignFocus();
  const isMobile = useIsMobile();

  // Focused mode is latched at mount. By the time this step renders, QuoteApp
  // has gated on !isLoading and the provider's restore flags (quoteShared /
  // quoteReloaded) are settled, so mount-time eligibility is authoritative.
  // Live `eligible` can flip false later (interacting with the step can set
  // selectedProcedureTypes) and reacting to that would unmount/remount the
  // step mid-interaction.
  const [enrolled] = useState(() => focus.eligible);

  // Focus params present but unusable at mount (typo'd/stale values,
  // >MAX_FOCUS_PROCEDURES, resume/shared session) — fall back silently but
  // flag the URL. Deduped per session+params so back-navigation doesn't turn
  // one bad link into an event "spike".
  useEffect(() => {
    if (enrolled || !focus.hasFocusParams) return;
    const paramsKey = [
      ...focus.focusTypesParam,
      "|",
      ...focus.focusProceduresParam,
    ].join(",");
    if (!oncePerSession(`focus-fallback:${paramsKey}`)) return;
    trackQuoteFocusFallback({
      focusTypesParam: focus.focusTypesParam,
      focusProceduresParam: focus.focusProceduresParam,
      resolvedCount: focus.focusProcedures.length,
      surface: isMobile ? "mobile" : "desktop",
    });
    // Mount-time decision — intentionally not reactive.
    // eslint-disable-next-line
  }, []);

  if (!enrolled) {
    return <StepSelectProcedureV2 next={next} />;
  }

  return <StepSelectProcedureV2Treatment next={next} focus={focus} />;
};

export default StepSelectProcedure;
